import { Listbox as HeadlessListbox } from "@headlessui/react";
import { Fragment } from "react";
import { Check } from "lucide-react";

interface Option<T> {
  value: T;
  label: string;
}

interface SingleSelectProps<T> {
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  multiple?: false;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
}

interface MultiSelectProps<T> {
  options: Option<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  multiple: true;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
}

type ListboxProps<T> = SingleSelectProps<T> | MultiSelectProps<T>;

export function Listbox<T extends string | number>(props: ListboxProps<T>) {
  const { options, label, disabled = false, multiple, placeholder } = props;

  if (multiple) {
    const { value, onChange } = props as MultiSelectProps<T>;
    const selectedOptions = options.filter((o) => value.includes(o.value));

    return (
      <div className="w-full">
        {label && (
          <label className="block mb-1 text-sm font-medium">{label}</label>
        )}
        <HeadlessListbox
          value={value}
          onChange={onChange}
          disabled={disabled}
          multiple
        >
          <div className="relative text-sm">
            <HeadlessListbox.Button className="w-full rounded-md px-3 py-2 text-left bg-light border border-primary-200">
              {selectedOptions.length === 0
                ? placeholder || "Click to select..."
                : selectedOptions.length === 1
                ? selectedOptions[0].label
                : `${selectedOptions.length} selected`}
            </HeadlessListbox.Button>
            <HeadlessListbox.Options className="absolute z-10 mt-1 w-full bg-light border border-neutral-200 rounded-md shadow-lg max-h-60 overflow-auto">
              {options.map((option) => (
                <HeadlessListbox.Option
                  key={option.value}
                  value={option.value}
                  as={Fragment}
                >
                  {({ active, selected }) => (
                    <div
                      className={`text-sm cursor-pointer select-none px-4 py-2 transition-colors duration-150 flex items-center justify-between ${
                        active ? "bg-primary-100" : "hover:bg-neutral-50"
                      } ${selected ? "font-semibold" : ""}`}
                    >
                      <span>{option.label}</span>
                      {selected && (
                        <Check className="w-4 h-4 text-main flex-shrink-0" />
                      )}
                    </div>
                  )}
                </HeadlessListbox.Option>
              ))}
            </HeadlessListbox.Options>
          </div>
        </HeadlessListbox>
      </div>
    );
  } else {
    const { value, onChange } = props as SingleSelectProps<T>;
    const selected = options.find((o) => o.value === value);

    return (
      <div className="w-full">
        {label && (
          <label className="block mb-1 text-sm font-medium">{label}</label>
        )}
        <HeadlessListbox value={value} onChange={onChange} disabled={disabled}>
          <div className="relative text-sm">
            <HeadlessListbox.Button className="w-full rounded-md px-3 py-2 text-left bg-light border border-primary-200">
              {selected ? selected.label : placeholder || "Select..."}
            </HeadlessListbox.Button>
            <HeadlessListbox.Options className="absolute z-10 mt-1 w-full bg-light border border-neutral-200 rounded-md shadow-lg max-h-60 overflow-auto">
              {options.map((option) => (
                <HeadlessListbox.Option
                  key={option.value}
                  value={option.value}
                  as={Fragment}
                >
                  {({ active, selected }) => (
                    <div
                      className={`text-sm cursor-pointer select-none px-4 py-2 transition-colors duration-150 ${
                        active ? "bg-primary-100" : "hover:bg-neutral-50"
                      } ${selected ? "font-semibold" : ""}`}
                    >
                      {option.label}
                    </div>
                  )}
                </HeadlessListbox.Option>
              ))}
            </HeadlessListbox.Options>
          </div>
        </HeadlessListbox>
      </div>
    );
  }
}
